import { useMutation, useQuery } from "react-query";
import HttpService from "../../services/http";
import { toast } from "react-toastify";
import axios, { AxiosError } from "axios";
import { queryClient } from "../../providers/QueryProvider";
import { useTranslation } from "react-i18next";
import {
  ESignatureTemplate,
  ESignatureTemplatePreview,
  ConfirmTemplatePayload,
  CreateSigningRequestPayload,
} from "../../types/ESignatureTypes";

const publicHttp = axios.create({
  baseURL: process.env.REACT_APP_UMD_BACK_END_DOMAIN || "/umd-service/api",
});

export const SIGNING_AUTH_ERROR_CODE = "RSSEKO001";

const signingAuthHeaders = (
  login_token?: string | null
): Record<string, string> =>
  login_token ? { "x-login-token": login_token } : {};

export const useGetESignatureTemplates = (
  customer_identification: string,
  entityIdentification: string,
  disabled?: boolean
) => {
  return useQuery(
    ["esignature-templates", entityIdentification],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/esignature/templates`
      );
      return res?.data?.element as ESignatureTemplate[];
    },
    {
      enabled: !disabled && !!customer_identification && !!entityIdentification,
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || err?.message);
      },
    }
  );
};

export const useGetESignatureTemplate = (
  customer_identification: string,
  entityIdentification: string,
  templateId: string
) => {
  return useQuery(
    ["esignature-template", templateId],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/esignature/templates/${templateId}`
      );
      return res?.data?.element as ESignatureTemplate;
    },
    {
      enabled:
        !!customer_identification && !!entityIdentification && !!templateId,
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || err?.message);
      },
    }
  );
};

export const useGetESignatureTemplatePreviewUrl = () => {
  return useMutation<
    {
      signed_url: string;
      expires_in_minutes: number;
    },
    AxiosError<any, any>,
    {
      customer_identification: string;
      entityIdentification: string;
      templateId: string;
    }
  >(
    async ({ customer_identification, entityIdentification, templateId }) => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/esignature/templates/${templateId}/preview-url`
      );
      return res?.data?.element;
    },
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const usePreviewESignatureTemplate = () => {
  return useMutation<
    ESignatureTemplatePreview,
    AxiosError<any, any>,
    {
      customer_identification: string;
      entityIdentification: string;
      formData: FormData;
    }
  >(
    async ({ customer_identification, entityIdentification, formData }) => {
      const res = await HttpService.post(
        `/${customer_identification}/${entityIdentification}/esignature/templates/preview`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return res?.data?.element;
    },
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useConfirmESignatureTemplate = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: {
      customer_identification: string;
      entityIdentification: string;
      data: ConfirmTemplatePayload;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/esignature/templates/confirm`,
        data
      ),
    {
      onSuccess: (_res, variables) => {
        toast.success(t("esignature.templates.toast.createSuccess"));
        queryClient.invalidateQueries([
          "esignature-templates",
          variables.entityIdentification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCancelESignatureTemplatePreview = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      cdn_file_name,
    }: {
      customer_identification: string;
      entityIdentification: string;
      cdn_file_name: string;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/esignature/templates/cancel`,
        { cdn_file_name }
      ),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteESignatureTemplate = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      templateId,
    }: {
      customer_identification: string;
      entityIdentification: string;
      templateId: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/esignature/templates/${templateId}`
      ),
    {
      onSuccess: (_res, variables) => {
        toast.success(t("esignature.templates.toast.deleteSuccess"));
        queryClient.invalidateQueries([
          "esignature-templates",
          variables.entityIdentification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCreateSigningRequest = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: {
      customer_identification: string;
      entityIdentification: string;
      data: CreateSigningRequestPayload;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/esignature/requests`,
        data
      ),
    {
      onSuccess: (_res, variables) => {
        toast.success(t("esignature.documents.toast.createSuccess"));
        queryClient.invalidateQueries([
          "esignature-documents",
          variables.entityIdentification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetSigningPageData = (
  entity_identification: string,
  user_identification: string,
  signing_token: string,
  login_token: string | null
) => {
  return useQuery<any, AxiosError<any>>(
    ["signing-page", entity_identification, user_identification, signing_token],
    async () => {
      const res = await publicHttp.get(
        `/esignature/sign/${entity_identification}/${user_identification}/${signing_token}`,
        { headers: signingAuthHeaders(login_token) }
      );
      return res?.data?.element;
    },
    {
      enabled:
        !!entity_identification && !!user_identification && !!signing_token,
      retry: false,
    }
  );
};

export const useSubmitSignature = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      signing_token,
      login_token,
      data,
    }: {
      entity_identification: string;
      user_identification: string;
      signing_token: string;
      login_token: string | null;
      data: FormData;
    }) => {
      return publicHttp.post(
        `/esignature/sign/${entity_identification}/${user_identification}/${signing_token}/submit`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...signingAuthHeaders(login_token),
          },
        }
      );
    }
  );
};

export const useGeneratePreview = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      signing_token,
      login_token,
      input_values,
      choice_values,
    }: {
      entity_identification: string;
      user_identification: string;
      signing_token: string;
      login_token: string | null;
      input_values: Record<string, string>;
      choice_values?: Record<string, boolean>;
    }) => {
      return publicHttp.post(
        `/esignature/sign/${entity_identification}/${user_identification}/${signing_token}/preview`,
        { input_values, choice_values: choice_values || {} },
        { headers: signingAuthHeaders(login_token) }
      );
    }
  );
};

export const useDeclineSignature = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      signing_token,
      login_token,
      reason,
    }: {
      entity_identification: string;
      user_identification: string;
      signing_token: string;
      login_token: string | null;
      reason?: string;
    }) => {
      return publicHttp.post(
        `/esignature/sign/${entity_identification}/${user_identification}/${signing_token}/decline`,
        { reason },
        { headers: signingAuthHeaders(login_token) }
      );
    }
  );
};

export interface VerificationData {
  status: "signed" | "not_found";
  request_id?: string;
  document_title?: string;
  template_id?: string;
  signer_name?: string;
  signer_email?: string;
  signer_phone?: string;
  audit_trail?: {
    signed_at?: string;
    signer_ip?: string;
    is_email_verified?: boolean;
    is_phone_verified?: boolean;
    verified_phone_masked?: string | null;
    signature_type?: string;
    document_hash_sha256?: string | null;
    user?: {
      user_identification?: string;
      full_name?: string;
      email?: string;
      phone?: string;
    };
    entity?: {
      entity_identification?: string;
      entity_name?: string;
      entity_contact_email?: string;
    };
  };
  entity?: {
    entity_identification?: string;
    entity_name?: string;
    entity_contact_email?: string;
  };
}

export const useGetVerificationData = (
  entity_identification: string,
  user_identification: string,
  request_id: string
) => {
  return useQuery<VerificationData, AxiosError<any>>(
    [
      "esignature-verify",
      entity_identification,
      user_identification,
      request_id,
    ],
    async () => {
      const res = await publicHttp.get(
        `/esignature/verify/${entity_identification}/${user_identification}/${request_id}`
      );
      return res?.data?.element as VerificationData;
    },
    {
      enabled: !!entity_identification && !!user_identification && !!request_id,
      retry: false,
      onError: () => {},
    }
  );
};

export const useValidateLoginToken = (
  entity_identification: string,
  user_identification: string,
  login_token: string | null
) => {
  return useQuery<any, AxiosError<any>>(
    [
      "validate-login-token",
      entity_identification,
      user_identification,
      login_token,
    ],
    async () => {
      const res = await publicHttp.post(
        `/auth/${entity_identification}/${user_identification}/login-page/validation`,
        { login_token }
      );
      return res?.data?.element;
    },
    {
      enabled:
        !!entity_identification && !!user_identification && !!login_token,
      retry: false,
      staleTime: Infinity,
    }
  );
};

export const useListSigningRequests = (
  customer_identification: string,
  entityIdentification: string,
  user_identification: string,
  filters?: { status?: string; template_id?: string },
  disabled?: boolean
) => {
  const params = new URLSearchParams();
  params.append("user_identification", user_identification);
  if (filters?.status) params.append("status", filters.status);
  if (filters?.template_id) params.append("template_id", filters.template_id);

  return useQuery<any[], AxiosError<any>>(
    [
      "esignature-documents",
      entityIdentification,
      user_identification,
      filters?.status,
    ],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/esignature/requests?${params.toString()}`
      );
      return res?.data?.element || [];
    },
    {
      enabled:
        !disabled &&
        !!customer_identification &&
        !!entityIdentification &&
        !!user_identification,
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteSigningRequest = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      request_id,
      user_identification,
    }: {
      customer_identification: string;
      entityIdentification: string;
      request_id: string;
      user_identification: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/esignature/requests/${request_id}`,
        { data: { user_identification } }
      ),
    {
      onSuccess: (_res, variables) => {
        toast.success(t("esignature.documents.toast.deleteSuccess"));
        queryClient.invalidateQueries([
          "esignature-documents",
          variables.entityIdentification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCancelSigningRequest = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      request_id,
      user_identification,
    }: {
      customer_identification: string;
      entityIdentification: string;
      request_id: string;
      user_identification: string;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/esignature/requests/${request_id}/cancel`,
        { user_identification }
      ),
    {
      onSuccess: (_res, variables) => {
        toast.success(t("esignature.documents.toast.cancelSuccess"));
        queryClient.invalidateQueries([
          "esignature-documents",
          variables.entityIdentification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetSignedDocumentUrl = () => {
  return useMutation<
    { signed_url: string; expires_in_minutes: number },
    AxiosError<any, any>,
    {
      customer_identification: string;
      entityIdentification: string;
      request_id: string;
      user_identification: string;
    }
  >(
    async ({
      customer_identification,
      entityIdentification,
      request_id,
      user_identification,
    }) => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/esignature/requests/${request_id}/document-url?user_identification=${user_identification}`
      );
      return res?.data?.element;
    },
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useGetSigningLink = () => {
  return useMutation<
    { signing_url?: string; [key: string]: any },
    AxiosError<any, any>,
    {
      customer_identification: string;
      entityIdentification: string;
      request_id: string;
      user_identification: string;
    }
  >(
    async ({
      customer_identification,
      entityIdentification,
      request_id,
      user_identification,
    }) => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/esignature/requests/${request_id}/signing-link?user_identification=${user_identification}`
      );
      return res?.data?.element;
    },
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};
